import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  type MenuCheckedValueChangeData,
} from "@fluentui/react-components";
import { Dismiss16Regular, Filter16Regular, Search16Regular } from "@fluentui/react-icons";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { FuiButton, FuiSearchBox } from "@/components/fui-control";
import { PageHeaderTitle } from "@/components/ui-typography";
import { shellGutter } from "@/shell/content-frame";

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    minWidth: 0,
  },
  /** 필터 줄 위의 제목. 오른쪽은 그 화면의 본동사. */
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
    minWidth: 0,
  },
  headTable: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  heading: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  titleLine: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    maxWidth: "100%",
  },
  title: {
    margin: 0,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  titleAside: {
    display: "inline-flex",
    flexShrink: 0,
  },
  headAction: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  description: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
  },
  /** 제목과 도구 줄 사이. small 탭의 좌우 패딩만큼 당겨 아이콘을 제목에 맞춘다. */
  sections: {
    paddingLeft: `calc(${shellGutter} - ${tokens.spacingHorizontalSNudge})`,
    paddingRight: shellGutter,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    "& .fui-Tab": {
      fontWeight: tokens.fontWeightRegular,
    },
    "& .fui-Tab__content": {
      fontWeight: tokens.fontWeightRegular,
      whiteSpace: "nowrap",
    },
    "& .fui-Tab[aria-selected='true'] .fui-Tab__content": {
      fontWeight: tokens.fontWeightMedium,
    },
  },
  /** 도구 줄이 없는 상위 탭. 밑줄과 표 사이. 멤버 권한과 같다. */
  sectionGap: {
    height: tokens.spacingVerticalS,
  },
  sectionsTable: {
    paddingLeft: `calc(-1 * ${tokens.spacingHorizontalSNudge})`,
    paddingRight: 0,
  },
  bar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
    backgroundColor: "transparent",
    minHeight: "44px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  /** 바로 아래 표의 테두리와 좌우를 맞춘다. */
  tableEdge: {
    paddingLeft: 0,
    paddingRight: 0,
    backgroundColor: "transparent",
  },
  /** 검색과 필터. 좁으면 필터만 가로로 스크롤하고 검색과 오른쪽은 남긴다. */
  lead: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flex: "1 1 auto",
    minWidth: 0,
  },
  /** 범위·필터. */
  filters: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flex: "1 1 auto",
    minWidth: 0,
    flexWrap: "nowrap",
    overflowX: "auto",
    scrollbarWidth: "thin",
  },
  /** 기능 버튼과 보기 탭. 좁아져도 이 칸은 줄어들지 않는다. */
  trail: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
    marginLeft: "auto",
  },
  /** 보기 탭 오른쪽. 그 화면에서 하나만 두는 보조 동작. 본동사는 크롬에 둔다. */
  action: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  search: {
    width: "220px",
    maxWidth: "220px",
    flexShrink: 0,
  },
  nowrap: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  /** 「필터」 이름. 값은 그 오른쪽, 진한 글자. */
  filterName: {
    color: tokens.colorNeutralForeground3,
  },
  filterInfo: {
    display: "inline-block",
    maxWidth: "120px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
    color: tokens.colorNeutralForeground1,
  },
  menuHint: {
    display: "block",
    maxWidth: "120px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  /** 여러 축이 적용된 개수. 회색 라운드 칩. 버튼 호버 배경보다 한 단계 진하다. */
  filterCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    minWidth: "16px",
    height: "16px",
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground5,
    color: tokens.colorNeutralForeground2,
    fontSize: "11px",
    lineHeight: "16px",
    fontVariantNumeric: "tabular-nums",
  },
  selecting: {
    backgroundColor: tokens.colorBrandBackground2,
  },
});

/**
 * 화면 도구 줄. 전역 바(플랜 탭·본동사) 바로 아래에 둔다.
 * 제목이 있으면 그 아래에 필터 줄을 둔다. 제목 오른쪽은 본동사(headingAction).
 * 대상이 갈리는 섹션(sections)은 제목과 도구 줄 사이고, Fluent TabList size small이다.
 * 그 섹션 안의 같은 표를 좁히는 범위는 filters의 SegmentedControl이다. 범위가 없는 섹션에는 두지 않는다.
 * 왼쪽은 검색과 필터, 오른쪽은 기능 버튼(trail·tools)과 보기 탭(view).
 */
export function ListToolbar({
  title,
  description,
  titleAside,
  headingAction,
  sections,
  search,
  filters,
  trail,
  tools,
  view,
  action,
  selecting,
  edge = "page",
}: {
  /** 필터 줄 위의 화면 이름. */
  title?: string;
  /** 제목 글자 바로 옆. 파일 화면의 목업 사례. */
  titleAside?: ReactNode;
  /** 제목 아래 한 줄. 범위처럼 제목이 말하지 않는 사실만 적는다. */
  description?: string;
  /** 제목 줄 오른쪽. 그 화면의 본동사. `inHeader`로 상단 바와 겹치지 않게 한다. */
  headingAction?: ReactNode;
  /** 대상이 갈리는 상위 탭. 제목과 도구 줄 사이. TabList는 size small. 그 탭의 범위는 `filters`. */
  sections?: ReactNode;
  /** 필터 줄 왼쪽. 작업 검색처럼 목록을 좁히는 칸. */
  search?: ReactNode;
  filters?: ReactNode;
  trail?: ReactNode;
  /** 그 화면 전용 보조 도구(단가 숨기기, 내보내기, 더 보기 등). 보기 탭 왼쪽. */
  tools?: ReactNode;
  /** 화면을 가르는 보기 탭. 필터 줄 오른쪽 끝. */
  view?: ReactNode;
  /** 보기 탭 오른쪽. 선택 중에는 숨기는 화면 전용 요소. 본동사는 `usePageAction`을 쓴다. */
  action?: ReactNode;
  selecting?: boolean;
  /** page는 목록 페이지 여백. table은 테두리 있는 표의 글자 시작에 맞춘다. */
  edge?: "page" | "table";
}) {
  const styles = useStyles();
  const hasLead = search != null || filters != null;
  const hasTrail = trail != null || tools != null || view != null;
  const hasBar = hasLead || hasTrail || action != null || selecting;
  const bar = (
    <div className={mergeClasses(styles.bar, edge === "table" && styles.tableEdge, selecting && styles.selecting)}>
      {hasLead && (
        <div className={styles.lead}>
          {search}
          {filters != null && <div className={styles.filters}>{filters}</div>}
        </div>
      )}
      {hasTrail && (
        <div className={styles.trail}>
          {trail}
          {tools}
          {view}
        </div>
      )}
      {action != null && <div className={styles.action}>{action}</div>}
    </div>
  );
  if (title == null) return bar;
  return (
    <div className={styles.stack}>
      <div className={mergeClasses(styles.head, edge === "table" && styles.headTable)}>
        <div className={styles.heading}>
          <div className={styles.titleLine}>
            <PageHeaderTitle as="h1" size="large" className={styles.title} title={title}>
              {title}
            </PageHeaderTitle>
            {titleAside ? <div className={styles.titleAside}>{titleAside}</div> : null}
          </div>
          {description ? (
            <span className={styles.description} title={description}>
              {description}
            </span>
          ) : null}
        </div>
        {headingAction ? <div className={styles.headAction}>{headingAction}</div> : null}
      </div>
      {sections != null ? (
        <div className={mergeClasses(styles.sections, edge === "table" && styles.sectionsTable)}>{sections}</div>
      ) : null}
      {hasBar ? bar : sections != null ? <div className={styles.sectionGap} /> : null}
    </div>
  );
}

export function useListToolbarStyles() {
  return useStyles();
}

/** 목록 검색. 아이콘만 두고, 누르면 220px 칸이 열린다. 검색어가 있으면 접지 않는다. */
export function ListSearch({
  value,
  onChange,
  requestOpen = 0,
  label = "검색",
  placeholder = "검색",
}: {
  value: string;
  onChange: (value: string) => void;
  /** 값이 늘어나면 칸을 연다. */
  requestOpen?: number;
  label?: string;
  placeholder?: string;
}) {
  const styles = useStyles();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const expanded = open || value.length > 0;
  useEffect(() => {
    if (requestOpen) setOpen(true);
  }, [requestOpen]);
  useEffect(() => {
    if (value) return;
    const input = wrapRef.current?.querySelector("input");
    if (input && document.activeElement === input) return;
    setOpen(false);
  }, [value]);
  useEffect(() => {
    if (!expanded) return;
    wrapRef.current?.querySelector("input")?.focus();
  }, [expanded, requestOpen]);
  if (!expanded) {
    return (
      <Tooltip content={label} relationship="label">
        <FuiButton appearance="subtle" icon={<Search16Regular />} aria-label={label} onClick={() => setOpen(true)} />
      </Tooltip>
    );
  }
  return (
    <div
      ref={wrapRef}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) return;
        if (!value) setOpen(false);
      }}
    >
      <FuiSearchBox
        className={styles.search}
        placeholder={placeholder}
        value={value}
        onChange={(_, data) => onChange(data.value)}
        aria-label={label}
        onKeyDown={(event) => {
          if (event.key !== "Escape" || value) return;
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }}
      />
    </div>
  );
}

/** 필터 하위 목록의 뒤로 항목. 화면 글자는 항목 이름이고, 읽는 이름은 「상태로 돌아가기」다. */
export function menuBackLabel(title: string): string {
  const last = title.charCodeAt(title.length - 1);
  const syllable = last - 0xac00;
  const hangul = syllable >= 0 && syllable <= 11171;
  const batchim = hangul ? syllable % 28 : 0;
  const particle = batchim !== 0 && batchim !== 8 ? "으로" : "로";
  return `${title}${particle} 돌아가기`;
}

/**
 * 화면 필터 버튼의 글자. 아이콘과 함께 쓴다.
 * 한 축은 현재 값(필터: 전체). 여러 축은 적용된 개수(필터: 2)이고, 개수는 회색 라운드 칩이다.
 */
export function FilterLabel({ info, count }: { info?: string; count?: number }) {
  const styles = useStyles();
  const showCount = count != null && count > 0;
  if (!info && !showCount) return <>필터</>;
  return (
    <>
      <span className={styles.filterName}>필터: </span>
      {info ? <span className={styles.filterInfo}>{info}</span> : <span className={styles.filterCount}>{count}</span>}
    </>
  );
}

/**
 * 한 축만 고르는 목록 필터. 버튼은 아이콘과 「필터: 현재 값」이다.
 * 기본값이 아니면 메뉴 맨 아래에 필터 초기화가 생긴다.
 */
export function ListFilterMenu<T extends string>({
  label,
  value,
  idleValue,
  options,
  onChange,
}: {
  /** 메뉴가 무엇을 좁히는지. 버튼 이름은 항상 「필터」다. */
  label: string;
  value: T;
  idleValue: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  const styles = useStyles();
  const applied = value !== idleValue;
  const current = options.find((option) => option.value === value)?.label ?? value;
  const onChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    const next = data.checkedItems[0];
    if (next != null && next !== value) onChange(next as T);
  };

  return (
    <Menu checkedValues={{ filter: [value] }} onCheckedValueChange={onChecked}>
      <MenuTrigger disableButtonEnhancement>
        <FuiButton className={styles.nowrap} icon={<Filter16Regular />} appearance="subtle" aria-label={`필터: ${current}`}>
          <FilterLabel info={current} />
        </FuiButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList aria-label={label}>
          {options.map((option) => (
            <MenuItemRadio key={option.value} name="filter" value={option.value}>
              {option.label}
            </MenuItemRadio>
          ))}
          {applied ? (
            <>
              <MenuDivider />
              <MenuItem icon={<Dismiss16Regular />} onClick={() => onChange(idleValue)}>
                필터 초기화
              </MenuItem>
            </>
          ) : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
